import React, { useState, useEffect } from 'react';
import { employeesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

const roleClasses = {
  admin: 'bg-red-100 text-red-700',
  supervisor: 'bg-blue-100 text-blue-700',
  technician: 'bg-emerald-100 text-emerald-700',
};
const Badge = ({ status, map }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${(map || roleClasses)[status] || 'bg-slate-100 text-slate-600'}`}>{status}</span>
);

const inp = 'w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent';
const sel = `${inp} bg-white`;
const Label = ({ children, required }) => (
  <label className="block text-xs font-semibold text-slate-600 mb-1">{children}{required && <span className="text-red-500 ml-0.5">*</span>}</label>
);

const EmployeeManagement = () => {
  const [employees, setEmployees] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentEmp, setCurrentEmp] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const { isAdmin, isSupervisor } = useAuth();
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    employeeCode: '', firstName: '', lastName: '', email: '',
    password: '', role: 'technician', department: '', phone: ''
  });

  useEffect(() => { fetchEmployees(); }, []);
  useEffect(() => { filterEmployees(); }, [employees, searchTerm, roleFilter]);

  const fetchEmployees = async () => {
    try {
      const response = await employeesAPI.getAll();
      setEmployees(response.data.employees || []);
    } catch {
      toast('Failed to load employees', 'error');
    } finally {
      setLoading(false);
    }
  };

  const filterEmployees = () => {
    let result = [...employees];
    if (roleFilter !== 'all') result = result.filter(e => e.role === roleFilter);
    if (searchTerm) {
      result = result.filter(e =>
        e.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        e.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        e.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        e.employee_code?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    setFiltered(result);
  };

  const handleInputChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleAdd = () => {
    setEditMode(false); setCurrentEmp(null);
    setFormData({ employeeCode: '', firstName: '', lastName: '', email: '', password: '', role: 'technician', department: '', phone: '' });
    setShowModal(true);
  };

  const handleEdit = (emp) => {
    setEditMode(true); setCurrentEmp(emp);
    setFormData({
      employeeCode: emp.employee_code || '', firstName: emp.first_name || '',
      lastName: emp.last_name || '', email: emp.email || '', password: '',
      role: emp.role || 'technician', department: emp.department || '', phone: emp.phone || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editMode) {
        await employeesAPI.update(currentEmp._id, formData);
        toast('Employee updated successfully!', 'success');
      } else {
        await employeesAPI.create(formData);
        toast('Employee added successfully!', 'success');
      }
      setShowModal(false);
      fetchEmployees();
    } catch (err) {
      const e = err.response?.data?.error;
      toast(typeof e === 'string' ? e : e?.message || 'Operation failed', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivate = async (emp) => {
    if (!window.confirm(`Deactivate ${emp.first_name} ${emp.last_name}?`)) return;
    try {
      await employeesAPI.deactivate(emp._id);
      toast(`${emp.first_name} ${emp.last_name} deactivated`, 'warning');
      fetchEmployees();
    } catch {
      toast('Failed to deactivate employee', 'error');
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-96">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-brand-primary rounded-full animate-spin"></div>
        <p className="text-sm text-slate-500">Loading employees...</p>
      </div>
    </div>
  );

  return (
    <div className="space-y-5">
      {/* Action Bar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[220px] relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input type="text" placeholder="Search by name, email or employee code..."
            className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
        </div>
        <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)} className={`${sel} w-auto`}>
          <option value="all">All Roles</option>
          <option value="admin">Admin</option>
          <option value="supervisor">Supervisor</option>
          <option value="technician">Technician</option>
        </select>
        {(isAdmin || isSupervisor) && (
          <button onClick={handleAdd}
            className="flex items-center gap-2 px-4 py-2 bg-brand-primary hover:bg-brand-primary-dark text-white text-sm font-semibold rounded-lg transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Employee
          </button>
        )}
      </div>

      {/* Table Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <h3 className="font-semibold text-slate-900">Employees</h3>
          </div>
          <span className="px-2.5 py-0.5 bg-brand-primary-bg text-brand-primary text-xs font-semibold rounded-full">{filtered.length}</span>
        </div>

        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-2">
            <svg className="w-12 h-12 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <p className="text-slate-500 font-medium">No employees found</p>
            <p className="text-slate-400 text-sm">Try adjusting your filters</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  {['Employee', 'Code', 'Email', 'Department', 'Role', 'Phone', ...(isAdmin || isSupervisor ? ['Actions'] : [])].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map(emp => (
                  <tr key={emp._id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-primary to-brand-gold flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                          {emp.first_name?.[0]}{emp.last_name?.[0]}
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900 text-sm">{emp.first_name} {emp.last_name}</p>
                          <p className="text-xs text-slate-400">{emp.is_active ? 'Active' : 'Inactive'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3"><code className="text-xs bg-slate-100 px-2 py-0.5 rounded">{emp.employee_code}</code></td>
                    <td className="px-4 py-3 text-sm text-slate-700">{emp.email}</td>
                    <td className="px-4 py-3 text-sm text-slate-700">{emp.department || <span className="text-slate-400">—</span>}</td>
                    <td className="px-4 py-3"><Badge status={emp.role} map={roleClasses} /></td>
                    <td className="px-4 py-3 text-sm text-slate-700">{emp.phone || <span className="text-slate-400">—</span>}</td>
                    {(isAdmin || isSupervisor) && (
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button onClick={() => handleEdit(emp)} title="Edit"
                            className="p-1.5 text-slate-500 hover:text-brand-primary hover:bg-brand-primary-bg rounded-lg transition-colors">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          {isAdmin && emp.is_active && (
                            <button onClick={() => handleDeactivate(emp)} title="Deactivate"
                              className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h2 className="text-lg font-bold text-slate-900">{editMode ? 'Edit Employee' : 'Add New Employee'}</h2>
              <button onClick={() => setShowModal(false)} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
              <div className="overflow-y-auto flex-1 p-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label required>Employee Code</Label>
                    <input type="text" name="employeeCode" value={formData.employeeCode} onChange={handleInputChange} required disabled={editMode}
                      className={`${inp} ${editMode ? 'bg-slate-50 text-slate-400 cursor-not-allowed' : ''}`} />
                  </div>
                  <div>
                    <Label required>First Name</Label>
                    <input type="text" name="firstName" value={formData.firstName} onChange={handleInputChange} required className={inp} />
                  </div>
                  <div>
                    <Label required>Last Name</Label>
                    <input type="text" name="lastName" value={formData.lastName} onChange={handleInputChange} required className={inp} />
                  </div>
                  <div className="col-span-2">
                    <Label required>Email</Label>
                    <input type="email" name="email" value={formData.email} onChange={handleInputChange} required className={inp} />
                  </div>
                  {!editMode && (
                    <div className="col-span-2">
                      <Label required>Password</Label>
                      <input type="password" name="password" value={formData.password} onChange={handleInputChange} required className={inp} />
                    </div>
                  )}
                  <div>
                    <Label required>Role</Label>
                    <select name="role" value={formData.role} onChange={handleInputChange} className={sel}>
                      <option value="technician">Technician</option>
                      <option value="supervisor">Supervisor</option>
                      {isAdmin && <option value="admin">Admin</option>}
                    </select>
                  </div>
                  <div>
                    <Label>Department</Label>
                    <input type="text" name="department" value={formData.department} onChange={handleInputChange} className={inp} />
                  </div>
                  <div className="col-span-2">
                    <Label>Phone</Label>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} className={inp} />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-100 bg-slate-50 rounded-b-2xl">
                <button type="button" onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-slate-300 hover:bg-slate-100 text-slate-700 text-sm font-medium rounded-lg transition-colors">
                  Cancel
                </button>
                <button type="submit" disabled={submitting}
                  className="px-4 py-2 bg-brand-primary hover:bg-brand-primary-dark disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors">
                  {submitting ? 'Saving...' : editMode ? 'Update Employee' : 'Add Employee'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeManagement;
